import { getCurrentUrl } from '@ori/presentation-utils';
import { useCallback, useMemo, useState } from 'react';
import type { RevieveInitConfig } from 'revieve';

import { OPEN_QUERY_PARAM, OPEN_QUERY_PARAM_ALWAYS } from '../../constants';
import { useConfiguration } from '../../contexts';
import { useLocale, useRedirectToPdp, useRevieveAdvisor, useSessionStorage } from '../../hooks';
import { isAnalyticsConsentGranted, removeOpenQueryParam, setOpenQueryParam } from '../../utils';
import { STATE_DEFAULT, STATE_LOADING, STATE_RESTORED } from './constants';
import type { RevieveProps, RevieveState } from './types';
import { addToBasket, sendUserEvents, toggleFavorite } from './utils';

export const useRevieve = ({ novageSkincareConfiguration, openQueryParam }: RevieveProps) => {
  const { env, partnerId } = novageSkincareConfiguration;
  const canChangeOpenQueryParam = openQueryParam !== OPEN_QUERY_PARAM_ALWAYS;
  const redirectToPdp = useRedirectToPdp();
  const locale = useLocale();
  const [state, setState] = useState<RevieveState>(STATE_DEFAULT);
  const { tenant } = useConfiguration();
  const { close, open, reset } = useRevieveAdvisor({ novageSkincareConfiguration });
  const { restoreProduct, storeProduct } = useSessionStorage({
    novageSkincareConfiguration,
  });
  const clearOpenQueryParam = useCallback(() => {
    if (canChangeOpenQueryParam) {
      removeOpenQueryParam();
    }
  }, [canChangeOpenQueryParam]);
  const setupOpenQueryParam = useCallback(() => {
    if (canChangeOpenQueryParam) {
      setOpenQueryParam();
    }
  }, [canChangeOpenQueryParam]);
  const continueToPdp = useCallback(() => {
    const product = restoreProduct();
    if (product) {
      redirectToPdp(product);
    }
  }, [redirectToPdp, restoreProduct]);
  const handleClose = useCallback(() => {
    setState(STATE_DEFAULT);
    clearOpenQueryParam();
  }, [clearOpenQueryParam]);
  const handleOpen = useCallback(() => {
    setupOpenQueryParam();
    if (restoreProduct()) {
      setState(STATE_RESTORED);
    } else {
      open();
    }
  }, [setupOpenQueryParam, open, restoreProduct]);

  const initConfig = useMemo<RevieveInitConfig>(() => {
    const disableAnalytics = !isAnalyticsConsentGranted();

    return {
      env,
      locale,
      // eslint-disable-next-line @typescript-eslint/naming-convention -- 3rd party naming
      partner_id: partnerId,
      analytics: {
        disableUsageAnalytics: disableAnalytics,
        disableProductAnalytics: disableAnalytics,
        disableErrorReporting: disableAnalytics,
      },
      onClose: handleClose,
      onAddToCart: (data) => {
        if (Array.isArray(data)) {
          data.forEach(addToBasket);
        } else {
          addToBasket(data);
        }
      },
      onAddToFavorites: toggleFavorite,
      onCheckout: () => {
        const url = getCurrentUrl();
        url.pathname = '/shopping/basket';
        url.searchParams.delete(OPEN_QUERY_PARAM);
        window.location.assign(url);
      },
      onDisplayProducts: (historyId, products) => {
        // TODO -- Remove this once the behavior is configurable
        if (tenant === 'by') {
          return;
        }
        const product = products[0];
        if (product) {
          setupOpenQueryParam();
          storeProduct(product);
          redirectToPdp(product, 2000);
        }
      },
      onTrackingEvent: sendUserEvents,
      onFetchProducts: () => {
        // TODO -- Remove this once the behavior is configurable
        if (tenant === 'by') {
          return;
        }
        close();
        setState(STATE_LOADING);
      },
      onRemoveFromFavorites: toggleFavorite,
    };
  }, [
    close,
    env,
    handleClose,
    locale,
    partnerId,
    redirectToPdp,
    setupOpenQueryParam,
    storeProduct,
    tenant,
  ]);
  const onInit = useCallback(() => {
    if (openQueryParam) {
      handleOpen();
    }
  }, [handleOpen, openQueryParam]);
  const handleReset = useCallback(() => {
    setState(STATE_DEFAULT);
    reset(initConfig);
    handleOpen();
  }, [handleOpen, initConfig, reset]);

  return useMemo(
    () => ({
      close: handleClose,
      continueToPdp,
      initConfig,
      onInit,
      open: handleOpen,
      reset: handleReset,
      state,
    }),
    [continueToPdp, handleClose, handleOpen, handleReset, initConfig, onInit, state],
  );
};
